<script>
import { cloneDeep } from "lodash";
import StartEndTimes from "../../../../components/StartEndTimes.vue";
import Actions from "../Actions.vue";
import RadioInputGroup from "@/components/forms/RadioInputGroup";

const COURSE_DATES = "courseDates";

const AddCourseDetails = {
    data() {
        return {
            courseName: "",
            plainStartDate: "",
            plainEndDate: "",
            startDate: null,
            startDateWeekday: "",
            endDate: null,
            endDateWeekday: "",
            holidays: [],
            recurrenceType: "",
            errorStart: "",
            errorEnd: "",
            repetitionType: null,
            errorDate: "",
            errorCourseName: "",
            errorRepetitionType: "",

            start: null,
            end: null,
        };
    },
    name: "course-dates",
    components: {
        StartEndTimes,
        Actions,
        RadioInputGroup,
    },
    methods: {
        setDates(dates) {
            this.plainStartDate = dates.start.plain;
            this.startDate = dates.start.date;
            this.startDateWeekday = dates.start.weekday;
            this.plainEndDate = dates.end.plain;
            this.endDate = dates.end.date;
            this.endDateWeekday = dates.end.weekday;
        },
        setStore() {
            const dates = {
                courseName: this.courseName,
                plainStartDate: this.plainStartDate,
                startDate: this.startDate,
                startDateWeekday: this.startDateWeekday,
                plainEndDate: this.plainEndDate,
                endDate: this.endDate,
                endDateWeekday: this.endDateWeekday,
                holidays: this.holidays,
                repetition: this.repetitionType,
            };
            this.$storage.setStorageSync(COURSE_DATES, dates);
        },
        prevStep() {
            this.$router.push({ name: "Listing type" });
        },
        validation() {
            this.errorCourseName = "";
            this.errorDate = "";
            this.errorRepetitionType = "";

            // validate inputs
            if (this.courseName === "") {
                this.errorCourseName = "Course Name is required";
            }

            if (
                (this.startDate === null)
                || (this.startDate === "1/1/1970")
                || (this.endDate === null)
                || (this.startDate === "")
                || (this.endDate === "")
                || (this.endDate === "1/1/1970")
            ) {
                this.errorDate = "Start and End dates are required";
            }

            if (!this.errorDate) {
                const [startDay, startMonth, startYear] = this.startDate.split("/");
                const [endDay, endMonth, endYear] = this.endDate.split("/");

                const startDate = new Date(startYear, startMonth - 1, startDay);
                const endDate = new Date(endYear, endMonth - 1, endDay);

                if (startDate > endDate) {
                    this.errorDate = "Start Date must be before end date";
                }
            }

            if (this.repetitionType === null) {
                this.errorRepetitionType = "Please select repetition type";
            }

            if (this.errorCourseName || this.errorDate || this.errorRepetitionType) {
                return false;
            }

            return true;
        },
        submit() {
            if (this.validation()) {
                this.setStore();
                this.$router.push({ name: "Advanced Add Holiday" });
                return true;
            }

            return false;
        },

        clone(item) {
            return structuredClone(item);
        },
    },
    computed: {
        getReccurenceText() {
            if (this.courseName === "") {
                return "How often will the course dates recur?";
            }

            return `How often will the event dates in
            <span>${this.courseName}</span> recur?*`;
        },
    },
    mounted() {
        const course = this.$storage.getStorageSync(COURSE_DATES) || {};

        if (Object.keys(course).length > 0) {
            this.courseName = course.courseName;
            this.plainStartDate = course.plainStartDate;
            this.plainEndDate = course.plainEndDate;
            this.startDate = course.startDate;
            this.startDateWeekday = course.startDateWeekday;
            this.endDate = course.endDate;
            this.endDateWeekday = course.endDateWeekday;
            this.holidays = course.holidays;

            this.repetitionType = course.repetition;

            this.start = cloneDeep(new Date(course.plainStartDate));
            this.end = cloneDeep(new Date(course.plainEndDate));
        }
    },
};

export default AddCourseDetails;
</script>

<template>
    <div class="new-listing-advanced-flow">
        <div data-test-id="coursename">{{ courseName }}</div>
        <div class="row">
            <div class="col-md-12">
                <h2>Date Range</h2>
                <div class="lg-grey-txt mt-2">The date range is a container of time within which your events will
                    recur:
                </div>
                <div class="lg-grey-txt">
                    <ul>
                        <li class="mt-2">If you set a <strong>weekly recurrence</strong> then your events will start on
                            the first week of the date range and end on the last week of the date range.
                        </li>
                        <li class="mt-2">If you set a <strong>daily recurrence</strong> then your events will start on
                            the first day of the date range and end on the last day of the date range.
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <div class="d-flex flex-column course-name-gap">
            <label class="show">Course name<span class="required-field">*</span></label>
            <input
                v-model="courseName"
                data-test-id="advanced-course-name"
                data-test="input-course-name"
                maxlength="45"
                class="w-100"
                :class="{'error-input-border-rounded': errorCourseName}"
                type="text"
                placeholder="Enter a name (e.g. Autumn Camp)"
            />
            <div v-if="errorCourseName">
                <div class="error-text" data-test="validation-error">{{ errorCourseName }}</div>
            </div>
        </div>

        <div class="mt-3">
            <StartEndTimes
                data-test-id="advanced-start-end-times"
                @change="setDates"
                parent-page="mp"
                :start="start"
                :end="end"
                :index="0"
                :disabledDates="{
                    to: new Date()
                }"
                :errors="{
                    startDate: errorStart,
                    endDate: errorEnd
                }"
            />
            <div v-if="errorDate">
                <div class="error-text" data-test="validation-error">{{ errorDate }}</div>
            </div>
        </div>
        <div class="mt-5 font-weight-600">
            <div
                class="col-6-md"
                v-html="`${getReccurenceText}<span class='required-field'>*</span>`"
            >
            </div>
        </div>
        <div class="mt-2">
            <div class="col-6-md">
                <div data-test-id="advanced-day-week-toggle" class="d-flex flex-column" data-test="block-radio-repeat">
                    <RadioInputGroup
                        label="title"
                        dataTest="radio-repeat"
                        v-model="repetitionType"
                        :selected="repetitionType"
                        :list="[
                            {
                                title: 'Daily',
                                value: 'daily',
                                dataTest: 'radio-repeat-daily',
                            },
                            {
                                title: 'Weekly',
                                value: 'weekly',
                                dataTest: 'radio-repeat-weekly',
                            }
                        ]"
                    />
                </div>
                <div v-if="errorRepetitionType">
                    <div class="error-text" data-test="validation-error">{{ errorRepetitionType }}</div>
                </div>
            </div>
        </div>
        <div data-test-id="advanced-goto-simple" class="mt-5 goto-text">
            If you’d like to combine daily and weekly recurring events, create a
            separate course for each recurrance or use the
            <button
                @click="prevStep"
                data-test-id="advanced-goto-simple-button"
                class="border-0 bg-white font-weight-bold text-underline p-0"
            >
                simple event creation tool
            </button>
            .
        </div>
        <Actions
            class="fixed-bottom"
            actions-styles="alt-bottom-actions mx-0"
            :full-width="true"
            :show-spacer="true"
            additional-block-class="col-3"
            @prev="prevStep"
            @next="submit"
            back-button-text="Exit"
            :next-button-text="'Next'"
            :margin-top="'margin-top-dates'"
            :disabled="false"
        />
    </div>
</template>

<style scoped>
.course-name-gap {
    margin-top: 25px;
}

.font-weight-600 {
    font-weight: 600;
}

.goto-text {
    color: #636066;
    margin-bottom: 7rem;
}
</style>
